import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { Metrics } from 'components/insights/constants';
import { formatDaysValue } from 'components/insights/formatting';
import { colors } from 'styles/theme/colors';

import MetricDetailLayout from '../../../shared/slideout/MetricDetailLayout';
import { readGrandTotal, readGrandTotalOrZero } from '../../../shared/slideout/rollupHelpers';
import { StatTile } from '../../../shared/slideout/statTiles';
import { METRIC_CONFIGS, NetworkPerformanceMetrics } from '../constants';
import { type MetricHeaderProps } from '../NetworkPerformanceSlideout';

import { buildColumnChartOptions } from './chartHelpers';

export default function AvgStartOfCareHeader({
  loading,
  rollups,
  highestEntity,
  lowestEntity,
  entityRows,
  entityLabel,
}: MetricHeaderProps) {
  const overallAvg = readGrandTotal(rollups, Metrics.DAYS_TO_START_CARE_AVERAGE);
  const patientVolume = readGrandTotalOrZero(rollups, Metrics.LOCATION_EPISODE_ID_COUNT);

  const { options: chartOptions, scrollConfig } = useMemo(
    () =>
      buildColumnChartOptions({
        entityRows,
        seriesName: METRIC_CONFIGS[NetworkPerformanceMetrics.AVG_START_OF_CARE].label,
        color: colors.primaryBlue,
        yAxisFormat: '{value} days',
        tooltipFormatter: function (this: Highcharts.TooltipFormatterContextObject) {
          return `${this.key}: ${formatDaysValue(this.y ?? null)}`;
        },
      }),
    [entityRows]
  );

  return (
    <MetricDetailLayout
      tiles={
        <>
          <StatTile
            label={METRIC_CONFIGS[NetworkPerformanceMetrics.AVG_START_OF_CARE].label}
            loading={loading}
            primary={formatDaysValue(overallAvg)}
            addendum={`Based on patient volume of ${patientVolume}`}
          />
          <StatTile
            label='Fastest Start of Care Time'
            loading={loading}
            primary={lowestEntity?.name ?? '\u2014'}
            size='medium'
            addendum={lowestEntity ? formatDaysValue(lowestEntity.value) : undefined}
            tone='green'
          />
          <StatTile
            label='Slowest Start of Care Time'
            loading={loading}
            primary={highestEntity?.name ?? '\u2014'}
            size='medium'
            addendum={highestEntity ? formatDaysValue(highestEntity.value) : undefined}
            tone='red'
          />
        </>
      }
      chartTitle={`${METRIC_CONFIGS[NetworkPerformanceMetrics.AVG_START_OF_CARE].label} by ${entityLabel}`}
      chartLoading={loading}
      isEmpty={entityRows.length === 0}
      emptyMessage='No data to show for this time period.'
      scrollConfig={scrollConfig}>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </MetricDetailLayout>
  );
}
